import {
  IChecklist,
  IChecklistGroup,
  IChecklistField
} from '../../models/Checklist'
import { IIDAndName } from '../../models/Entity'
import {
  IIncidentCategory,
  IIncidentTypePermission,
  IncidentStatus
} from '../../models/Incident'
import { TFunction } from 'i18next'
import { TemplateType, TemplateTypes } from '../../models/Template'

export const STATUS_OPTIONS = ['Pending', 'Open', 'Resolved']

export const STATUS_LABEL_MAP = {
  '100': 'Pending',
  '200': 'Open',
  '300': 'Resolved'
}

export const STATUS_VALUE_MAP = {
  Pending: 100,
  Open: 200,
  Resolved: 300
}

const STATUS_DEFINITIONS = [
  {
    name: 'Pending',
    value: STATUS_VALUE_MAP['Pending'],
    key: 'customLabel_incidentStatusPending'
  },
  {
    name: 'Open',
    value: STATUS_VALUE_MAP['Open'],
    key: 'customLabel_incidentStatusOpen'
  },
  {
    name: 'Resolved',
    value: STATUS_VALUE_MAP['Resolved'],
    key: 'customLabel_incidentStatusResolved'
  }
]

export interface IIncidentCategoryV2 extends IIDAndName {
  automaticResolve?: boolean
  opener: boolean
  resolver: boolean
}

/**
 * Returns the value of Name of affected person if it is available,
 * otherwise returns New Incident
 *
 * @param checklist - The checklist to get the title for
 * @returns string - The incident title
 */
export const getIncidentTitle = (
  checklist: IChecklist,
  translate: TFunction
) => {
  const nameOfAffectedPersonField = getNameOfAffectedPersonField(checklist)

  // If the field exists and has a value, return it, otherwise return "New Incident"
  if (
    nameOfAffectedPersonField &&
    nameOfAffectedPersonField.value &&
    nameOfAffectedPersonField.value.trim()
  ) {
    return `${
      checklist.incidentNo ? `${checklist.incidentNo} -` : ''
    } ${nameOfAffectedPersonField.value.trim()}`
  }

  return `New ${translate('customLabel_incident')}`
}

export const getNameOfAffectedPersonField = (checklist: IChecklist) => {
  return checklist.groups[0]?.fields.find(
    (field: IChecklistField) => field.mappingField?.value === 'personName'
  )
}

export const getNameOfAffectedPersonQuestionIndex = (
  fields: IChecklistField[]
): number => {
  return fields.findIndex(
    (field: IChecklistField) => field.mappingField?.value === 'personName'
  )
}

export const getSelectedCategoryObject = (
  selectedCategory: number,
  categories: IIncidentCategoryV2[]
): IIncidentCategoryV2 | undefined => {
  return categories.find(
    (category: IIncidentCategoryV2) => category.id === selectedCategory
  )
}

export const getSelectedStatusValue = (selectedStatus: string) => {
  return IncidentStatus[selectedStatus as keyof typeof IncidentStatus]
}

export const getTranslatedStatusLabel = (
  status: string,
  translate: TFunction
) => {
  if (status === 'Pending' || status === String(IncidentStatus.Pending)) {
    return translate('customLabel_incidentStatusPending')
  }
  if (status === 'Open' || status === String(IncidentStatus.Open)) {
    return translate('customLabel_incidentStatusOpen')
  }
  return translate('customLabel_incidentStatusResolved')
}

export const modifyStatusSelection = (
  selectedCategory: number,
  categories: IIncidentCategoryV2[],
  translate: TFunction,
  currentStatus?: string | number
): Record<string, string | number | boolean>[] => {
  const categoryObject =
    selectedCategory && categories.length
      ? getSelectedCategoryObject(selectedCategory, categories)
      : null

  const isAllowed = (name: string) => {
    if (!categoryObject) return true
    if (name === 'Pending') return true
    if (name === 'Open') return !!categoryObject.opener
    if (name === 'Resolved') return !!categoryObject.resolver
    return false
  }

  const statuses: { value: number; label: string; disabled?: boolean }[] =
    STATUS_DEFINITIONS.filter(({ name }) => isAllowed(name)).map(
      ({ value, key }) => ({ value, label: translate(key) })
    )

  if (currentStatus !== undefined) {
    const alreadyIncluded = statuses.some(
      (s) => s.value === Number(currentStatus)
    )
    if (!alreadyIncluded) {
      const statusToAdd = {
        value: Number(currentStatus),
        label: getTranslatedStatusLabel(String(currentStatus), translate),
        disabled: true
      }
      const insertIndex = statuses.findIndex(
        (s) => s.value > Number(currentStatus)
      )
      insertIndex === -1
        ? statuses.push(statusToAdd)
        : statuses.splice(insertIndex, 0, statusToAdd)
    }
  }

  return statuses
}

export const hasHazardSystemField = (checklist: IChecklist) => {
  return checklist.groups[0]?.fields.some(
    (field: IChecklistField) => field.mappingField?.value === 'hazard'
  )
}

export const getHazardSystemField = (checklist: IChecklist) => {
  return checklist.groups[0]?.fields.find(
    (field: IChecklistField) => field.mappingField?.value === 'hazard'
  )
}

export const isHazardSystemField = (field: IChecklistField) => {
  return field.mappingField?.value === 'hazard'
}

// add the selected hazard if any to the options list
export const includeHazardOptionsToChecklist = (
  checklist: IChecklist,
  hazards: IIDAndName[],
  offline: boolean
): IChecklist => {
  // Create a deep copy of the checklist
  const updatedChecklist = {
    ...checklist,
    groups: checklist.groups.map((group, index) => {
      if (index === 0) {
        return {
          ...group,
          fields: group.fields.map((field: IChecklistField) => {
            if (isHazardSystemField(field)) {
              return {
                ...field,
                offline,
                options: [
                  ...hazards.map((hazard) => ({
                    value: String(hazard.id),
                    label: hazard.name
                  })),
                  ...(checklist.hazard &&
                  !hazards.some((h) => h.name === checklist.hazard!.name)
                    ? [
                        {
                          value: String(checklist.hazard.id),
                          label: checklist.hazard.name
                        }
                      ]
                    : [])
                ]
              }
            }
            return { ...field } // Create new reference for each field
          })
        }
      }
      return {
        ...group,
        fields: [...group.fields] // Create new references for other groups
      }
    })
  }

  return updatedChecklist
}

export const getIncidentStatusLabel = (
  status: number,
  translate: TFunction
): string => {
  return getTranslatedStatusLabel(
    STATUS_LABEL_MAP[String(status) as keyof typeof STATUS_LABEL_MAP],
    translate
  )
}

export const hasIncidentTabs = (incident: IChecklist) => {
  return incident.groups.some(
    (group: IChecklistGroup) => group.detailsTab === false
  )
}

export const getAllDetailsTabs = (incident: IChecklist) => {
  return incident.groups
    .filter((group: IChecklistGroup) => group.detailsTab !== false)
    .map((group: IChecklistGroup) => ({
      id: group.id, // DEV NOTES: We can use this to map the correct detail group content
      label: group.label,
      checked: false
    }))
}

export const getAllIncidentTabs = (incident: IChecklist) => {
  return incident.groups
    .filter((group: IChecklistGroup) => group.detailsTab === false)
    .map((group: IChecklistGroup) => ({
      label: group.label,
      checked: false
    }))
}

export const isIncidentField = (field: IChecklistField) => {
  return field && field.hasOwnProperty('mappingField')
}

// Add this helper method to your Checklist class
export const validateIncidentDetails = (
  templateType: TemplateType,
  incident: IChecklist
): boolean => {
  if (templateType !== TemplateTypes.INCIDENT) {
    return false // Not an incident, no incomplete fields
  }

  // Check mandatory incident fields
  let hasIncompleteFields = false

  // Check if category is required and not set
  if (
    incident.incidentTypePermission?.accessIncidentCategory &&
    !incident.incidentCategory
  ) {
    hasIncompleteFields = true
  }

  // Check if status is not set
  if (!incident.status) {
    hasIncompleteFields = true
  }

  // Check if people are mandatory and not filled
  if (
    incident.usePeople &&
    incident.mandatoryPeople &&
    (!incident.people || incident.people.length === 0)
  ) {
    hasIncompleteFields = true
  }

  return hasIncompleteFields
}

export function shouldDisableCategoryField(
  existingIncident: boolean,
  selectedCategoryId: number,
  categories: IIncidentCategory[],
  incidentTypePermission?: IIncidentTypePermission
): boolean {
  // new incident do not disable category field
  if (!existingIncident) {
    return false
  }

  const id = Number(selectedCategoryId)

  // no selection yet, user has access to all categories, or the selected category is a placeholder
  if (
    !id ||
    incidentTypePermission?.accessIncidentCategory ||
    (id === -1 && categories.length > 1)
  ) {
    return false
  }

  // if user has no access to the selected category
  const hasAccessToCategory = categories.some((category) => category.id === id)

  return !hasAccessToCategory
}
